import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Slider,
  Switch,
  Typography,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import CircularSlider from 'react-circular-slider-svg';
import { CircularSliderWithChildren } from 'react-circular-slider-svg';
import { alertWarn } from '../../../alertDispatcher';
import { MapType } from '../helpers/mapTypes';
import { dispatchMissionUpdated, MISSION_EVENTS } from '../../../missionEvents';
import { dispatchCoresNeedUpdate } from '../../../mapEvents';
import EventBus from '../../../EventBus';
import {
  SMART_PATH_GRID_CELL_SIZE_METERS_MIN,
  SMART_PATH_GRID_CELL_SIZE_METERS_DEFAULT,
  SMART_PATH_GRID_CELL_SIZE_METERS_MAX,
  SMART_PATH_PREFERRED_ANGLE_DEGREES_DEFAULT,
  SMART_PATH_PREFERRED_ANGLE_PENALTY_FACTOR_DEFAULT,
  SMART_PATH_PREFERRED_ANGLE_PENALTY_FACTOR_MAX,
  SMART_PATH_BORDER_CLUSTER_PENALTY_FACTOR_DEFAULT,
  SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_DEFAULT,
  SMART_PATH_BORDER_CLUSTER_PENALTY_FACTOR_MAX,
  SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_MAX,
  SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_MAX,
  SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT,
  SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_DEFAULT_FOR_INTERLEAVING,
  SMART_PATH_CRUISE_SPEED_MPS_DEFAULT,
  SMART_PATH_CRUISE_SPEED_MPS_MIN,
  SMART_PATH_CRUISE_SPEED_MPS_MAX,
  SMART_PATH_TURN_SPEED_FACTOR_DEFAULT,
  SMART_PATH_TURN_SPEED_FACTOR_MIN,
  SMART_PATH_TURN_SPEED_FACTOR_MAX,
  SMART_PATH_DGP_ITERATION_CAP_DEFAULT,
  SMART_PATH_DGP_ITERATION_CAP_MAX,
  SMART_PATH_HYBRID_ASTAR_NODE_CAP_DEFAULT,
  SMART_PATH_HYBRID_ASTAR_NODE_CAP_MAX,
  SMART_PATH_HYBRID_ASTAR_TIME_BUDGET_MS_DEFAULT,
  SMART_PATH_HYBRID_ASTAR_TIME_BUDGET_MS_MAX,
  SMART_PATH_SHARP_TURN_WEIGHT_DEFAULT,
  SMART_PATH_SHARP_TURN_WEIGHT_MAX,
} from '../../../constants';
import { Mission } from '../../../db';
import { CustomCheckbox } from '../../utils/CustomCheckbox';
import { resolveTurnRadius, WheelType, WHEEL_TYPE_OPTIONS } from '../../../services/paths/robotPath/turnRadius';
import { DubinsWheelType } from '../../../db/local_storage';
import { dispatchPathUpdated } from '../../../mapEvents';
import type { DebugGeoJsonMode, SmartPathBuildResult } from '../../../services/paths/SmartPathBuilder';
import { TRACE_VIZ_BLOB_KEY, TRACE_VIZ_META_KEY } from '../../../services/paths/SmartPathBuilder';
import { SettingsAccessLevel } from '../../../types/types';
import DirectionDial from '../../utils/DirectionDial';

type SmartPathFormProps = {
  addCheckpoint: (name: string) => void;
  onCompletion: () => void;
  mapType: MapType;
  mission: Mission;
  accessLevel: SettingsAccessLevel;
};

const ACCURACY_OPTIONS = [
  { label: 'Coarse', value: SMART_PATH_GRID_CELL_SIZE_METERS_MAX },
  { label: 'Medium', value: SMART_PATH_GRID_CELL_SIZE_METERS_DEFAULT },
  { label: 'High', value: SMART_PATH_GRID_CELL_SIZE_METERS_MIN },
] as const;

type Accuracy = (typeof ACCURACY_OPTIONS)[number]['value'];

const SmartPathForm: React.FC<SmartPathFormProps> = ({
  addCheckpoint,
  onCompletion,
  mapType,
  mission,
  accessLevel,
}) => {
  const isZoneMission = mission.is_zone_mission();
  const isZoneInterleaveMission = mission.is_zone_interleave_mission();

  const [preferredDirectionEnabled, setPreferredDirectionEnabled] = useState(false);
  const [advancedTuningEnabled, setAdvancedTuningEnabled] = useState(false);
  const [preferredAngleDegrees, setPreferredAngleDegrees] = useState(SMART_PATH_PREFERRED_ANGLE_DEGREES_DEFAULT);
  const [preferredAnglePenaltyFactor, setPreferredAnglePenaltyFactor] = useState(0);

  const handlePreferredDirectionToggle = (enabled: boolean) => {
    setPreferredDirectionEnabled(enabled);
    setPreferredAnglePenaltyFactor(enabled ? SMART_PATH_PREFERRED_ANGLE_PENALTY_FACTOR_DEFAULT : 0);
  };

  const [gridCellSizeMeters, setGridCellSizeMeters] = useState(SMART_PATH_GRID_CELL_SIZE_METERS_DEFAULT);
  const [borderClusterPenaltyFactor, setBorderClusterPenaltyFactor] = useState(
    SMART_PATH_BORDER_CLUSTER_PENALTY_FACTOR_DEFAULT,
  );

  const defaultClusterPenaltyFactor = isZoneInterleaveMission
    ? SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_DEFAULT_FOR_INTERLEAVING
    : SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_DEFAULT;

  const [zoneClusterPenaltyFactor, setZoneClusterPenaltyFactor] = useState(defaultClusterPenaltyFactor);

  const [minDistanceToBoundaryMeters, setMinDistanceToBoundaryMeters] = useState(
    SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT,
  );
  const [cruiseSpeedMps, setCruiseSpeedMps] = useState(SMART_PATH_CRUISE_SPEED_MPS_DEFAULT);
  const [turnSpeedFactor, setTurnSpeedFactor] = useState(SMART_PATH_TURN_SPEED_FACTOR_DEFAULT);
  const [sharpTurnWeight, setSharpTurnWeight] = useState(SMART_PATH_SHARP_TURN_WEIGHT_DEFAULT);
  const [dgpIterationCap, setDgpIterationCap] = useState(SMART_PATH_DGP_ITERATION_CAP_DEFAULT);
  const [hybridAstarNodeCap, setHybridAstarNodeCap] = useState(SMART_PATH_HYBRID_ASTAR_NODE_CAP_DEFAULT);
  const [hybridAstarTimeBudgetMs, setHybridAstarTimeBudgetMs] = useState(
    SMART_PATH_HYBRID_ASTAR_TIME_BUDGET_MS_DEFAULT,
  );
  const [debugMode, setDebugMode] = useState<DebugGeoJsonMode>(
    accessLevel === 'Technician' ? 'layer_with_trace' : 'off',
  );
  // DubinsWheelType is persisted in localStorage and read by the pathfinder
  // request (the planner's turn radius) and by the robot-path overlay. Mirror it
  // into form state so the UI updates and so a change can fire
  // dispatchPathUpdated to refresh the overlay without a full path recompute.
  const [wheelType, setWheelType] = useState<WheelType>(DubinsWheelType.get() as WheelType);

  const handleWheelTypeChange = (next: WheelType) => {
    DubinsWheelType.set(next);
    setWheelType(next);
    dispatchPathUpdated();
  };

  const [lastResult, setLastResult] = useState<SmartPathBuildResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const startPosition: { lat: number; lon: number } | undefined = mission.getStartingPosition();

    if (!startPosition) {
      alertWarn('Starting position could not be determined');
      setSubmitting(false);

      return;
    }

    const endPosition: { lat: number; lon: number } | undefined = mission.getEndPosition();

    if (!endPosition) {
      alertWarn('End position could not be determined');
      setSubmitting(false);

      return;
    }

    addCheckpoint(`Smart Path External`);

    if (debugMode !== 'layer' && debugMode !== 'layer_with_trace') {
      EventBus.dispatch(MISSION_EVENTS.DEBUG_GEOJSON_UPDATED, null);
    }

    const result = await mission.buildSmartPath(
      startPosition.lat,
      startPosition.lon,
      endPosition.lat,
      endPosition.lon,
      {
        preferredAngleDegrees,
        preferredAnglePenaltyFactor: preferredDirectionEnabled ? preferredAnglePenaltyFactor : 0,
        gridCellSizeMeters,
        borderClusterPenaltyFactor,
        zoneClusterPenaltyFactor,
        keepCurrentIntermediateWaypoints: false,
        minDistanceToBoundaryMeters,
        turnRadiusMeters: resolveTurnRadius(wheelType),
        cruiseSpeedMps,
        turnSpeedFactor,
        sharpTurnWeight,
        dgpIterationCap,
        hybridAstarNodeCap,
        hybridAstarTimeBudgetMs,
        debugGeoJson: debugMode,
      },
    );
    setLastResult(result ?? null);

    if (result?.defectiveGeometries || result?.warningGeometries) {
      EventBus.dispatch(MISSION_EVENTS.DEFECTIVE_GEOMETRIES_UPDATED, {
        defectiveGeometries: result.defectiveGeometries || [],
        warningGeometries: result.warningGeometries || [],
      });
    }

    dispatchMissionUpdated();
    // buildSmartPath → calculate_path_restore_cores → calculate_path
    // already fires dispatchCoresNeedUpdate() internally.

    setSubmitting(false);

    onCompletion();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box p={2} maxWidth="100%" boxSizing="border-box">
        <Grid container spacing={3} direction="column" size={12}>
          <Grid>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Switch
                checked={preferredDirectionEnabled}
                onChange={(e) => handlePreferredDirectionToggle(e.target.checked)}
                color="primary"
              />
              <Typography variant="subtitle1">Preferred Direction of Traveling</Typography>
            </Box>

            {preferredDirectionEnabled && (
              <DirectionDial angleDegrees={preferredAngleDegrees} onChange={setPreferredAngleDegrees} />
            )}
          </Grid>

          {preferredDirectionEnabled && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Preferred Angle Penalty Factor
              </Typography>

              <Slider
                value={preferredAnglePenaltyFactor}
                onChange={(_e, value) => setPreferredAnglePenaltyFactor(value as number)}
                min={0}
                max={SMART_PATH_PREFERRED_ANGLE_PENALTY_FACTOR_MAX}
                step={0.1}
                valueLabelDisplay="auto"
                aria-labelledby="preferred-angle-penalty-factor-slider"
              />
            </Grid>
          )}

          <Grid>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Switch
                checked={advancedTuningEnabled}
                onChange={(e) => setAdvancedTuningEnabled(e.target.checked)}
                color="primary"
              />
              <Typography variant="subtitle1">Advanced tuning</Typography>
            </Box>
          </Grid>

          {advancedTuningEnabled && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Border Cluster Penalty Factor
              </Typography>
              <Slider
                value={borderClusterPenaltyFactor}
                onChange={(_e, value) => setBorderClusterPenaltyFactor(value as number)}
                min={0}
                max={SMART_PATH_BORDER_CLUSTER_PENALTY_FACTOR_MAX}
                step={1}
                valueLabelDisplay="auto"
                aria-labelledby="border-cluster-penalty-factor-slider"
              />
            </Grid>
          )}

          {advancedTuningEnabled && isZoneMission && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Zone Cluster Penalty Factor
              </Typography>

              <Slider
                value={zoneClusterPenaltyFactor}
                onChange={(_e, value) => {
                  setZoneClusterPenaltyFactor(value as number);
                }}
                min={0}
                max={SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_MAX}
                step={1}
                valueLabelDisplay="auto"
                aria-labelledby="zone-cluster-penalty-factor-slider"
              />
            </Grid>
          )}

          {advancedTuningEnabled && (
            <Grid>
              <Box display="flex" justifyContent="flex-start" alignItems="center" width="100%" style={{ gap: 8 }}>
                <Typography variant="subtitle1">Accuracy & Smoothness</Typography>

                <Select
                  label="Accuracy"
                  value={gridCellSizeMeters}
                  onChange={(e) => {
                    setGridCellSizeMeters(e.target.value as Accuracy);
                  }}
                >
                  {ACCURACY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Grid>
          )}

          {advancedTuningEnabled && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Min Distance to Boundary (meters)
              </Typography>

              <Slider
                value={minDistanceToBoundaryMeters}
                onChange={(_e, value) => setMinDistanceToBoundaryMeters(value as number)}
                min={0}
                max={SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_MAX}
                step={0.5}
                valueLabelDisplay="auto"
                aria-labelledby="min-distance-to-boundary-slider"
              />
            </Grid>
          )}

          {advancedTuningEnabled && accessLevel === 'Technician' && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Cruise Speed (m/s)
              </Typography>
              <Slider
                value={cruiseSpeedMps}
                onChange={(_e, v) => setCruiseSpeedMps(v as number)}
                min={SMART_PATH_CRUISE_SPEED_MPS_MIN}
                max={SMART_PATH_CRUISE_SPEED_MPS_MAX}
                step={0.1}
                valueLabelDisplay="auto"
              />
            </Grid>
          )}

          {advancedTuningEnabled && accessLevel === 'Technician' && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Turn Speed Factor (fraction of cruise while turning)
              </Typography>
              <Slider
                value={turnSpeedFactor}
                onChange={(_e, v) => setTurnSpeedFactor(v as number)}
                min={SMART_PATH_TURN_SPEED_FACTOR_MIN}
                max={SMART_PATH_TURN_SPEED_FACTOR_MAX}
                step={0.05}
                valueLabelDisplay="auto"
              />
            </Grid>
          )}

          {advancedTuningEnabled && accessLevel === 'Technician' && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Sharp Turn Weight
              </Typography>
              <Slider
                value={sharpTurnWeight}
                onChange={(_e, v) => setSharpTurnWeight(v as number)}
                min={0}
                max={SMART_PATH_SHARP_TURN_WEIGHT_MAX}
                step={0.5}
                valueLabelDisplay="auto"
              />
            </Grid>
          )}

          {advancedTuningEnabled && accessLevel === 'Technician' && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                DGP Iteration Cap ({dgpIterationCap})
              </Typography>
              <Slider
                value={dgpIterationCap}
                onChange={(_e, v) => setDgpIterationCap(v as number)}
                min={1}
                max={SMART_PATH_DGP_ITERATION_CAP_MAX}
                step={1}
                valueLabelDisplay="auto"
              />
            </Grid>
          )}

          {advancedTuningEnabled && accessLevel === 'Technician' && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Hybrid A* Node Cap ({hybridAstarNodeCap})
              </Typography>
              {/* Per-leg cap on lattice nodes the Hybrid A* solver expands
                  before falling through to DGP. 5000 is the pathfinder
                  default; raise for pathological obstacle topologies. */}
              <Slider
                value={hybridAstarNodeCap}
                onChange={(_e, v) => setHybridAstarNodeCap(v as number)}
                min={500}
                max={SMART_PATH_HYBRID_ASTAR_NODE_CAP_MAX}
                step={500}
                valueLabelDisplay="auto"
              />
            </Grid>
          )}

          {advancedTuningEnabled && accessLevel === 'Technician' && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Hybrid A* Time Budget ({hybridAstarTimeBudgetMs} ms)
              </Typography>
              {/* Wall-clock budget per leg. Whichever cap trips first
                  (node-expansion or wall-clock) ends the attempt and falls
                  through to DGP. 800 ms is the pathfinder default; raise
                  cautiously since it directly affects per-leg planning
                  latency. */}
              <Slider
                value={hybridAstarTimeBudgetMs}
                onChange={(_e, v) => setHybridAstarTimeBudgetMs(v as number)}
                min={100}
                max={SMART_PATH_HYBRID_ASTAR_TIME_BUDGET_MS_MAX}
                step={100}
                valueLabelDisplay="auto"
              />
            </Grid>
          )}

          {advancedTuningEnabled && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Drive Type
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={wheelType}
                onChange={(_e, v) => v && handleWheelTypeChange(v as WheelType)}
                size="small"
                aria-label="robot drive type"
              >
                {WHEEL_TYPE_OPTIONS.map((opt) => (
                  <ToggleButton key={opt} value={opt} aria-label={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
          )}

          {advancedTuningEnabled && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Debug GeoJSON
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={debugMode}
                onChange={(_e, v) => v && setDebugMode(v as DebugGeoJsonMode)}
                size="small"
                aria-label="debug geojson output mode"
              >
                <ToggleButton value="off" aria-label="off">
                  Off
                </ToggleButton>
                <ToggleButton value="download" aria-label="download file">
                  Download
                </ToggleButton>
                <ToggleButton value="layer" aria-label="show as map layer">
                  Layer
                </ToggleButton>
                <ToggleButton value="layer_with_trace" aria-label="show as map layer with per-leg trace">
                  Layer + trace
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}

          {accessLevel === 'Technician' && (
            <Grid>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  // The most recent "Layer + trace" SmartPath run stashes
                  // the GeoJSON blob in localStorage; the viz tab pulls
                  // it out (and clears the key) on load. If nothing's
                  // there, the user hasn't run with that debug mode yet.
                  if (!localStorage.getItem(TRACE_VIZ_BLOB_KEY)) {
                    alertWarn('No trace stored yet — run SmartPath with "Layer + trace" first.');

                    return;
                  }

                  window.open('/docs/hybrid-astar-viz.html?from=storage', '_blank');
                }}
              >
                Open trace viz
              </Button>
              <Typography variant="caption" display="block" color="textSecondary">
                Opens the most recent "Layer + trace" run in the Hybrid A* trace visualizer.
                {(() => {
                  const meta = localStorage.getItem(TRACE_VIZ_META_KEY);

                  if (!meta) {
                    return '';
                  }

                  try {
                    const m = JSON.parse(meta) as { ts?: number; n_legs?: number };

                    if (typeof m.ts !== 'number') {
                      return '';
                    }

                    const age_s = Math.round((Date.now() - m.ts) / 1000);
                    const legs = typeof m.n_legs === 'number' ? `${m.n_legs} legs, ` : '';

                    return ` (last: ${legs}${age_s}s ago)`;
                  } catch {
                    return '';
                  }
                })()}
              </Typography>
            </Grid>
          )}

          {lastResult && (
            <Grid>
              <Box
                style={{
                  backgroundColor: '#f4f4f4',
                  padding: 8,
                  borderRadius: 4,
                  fontSize: 13,
                }}
              >
                <div>
                  <b>Model:</b> {lastResult.costModel ?? 'n/a'}
                </div>
                <div>
                  <b>Distance:</b> {lastResult.totalPhysicalDistance?.toFixed(0) ?? '—'} m
                </div>
                <div>
                  <b>Time:</b> {lastResult.totalTimeSec?.toFixed(1) ?? '—'} s
                  {lastResult.totalTimeSec !== undefined && ` (≈ ${(lastResult.totalTimeSec / 60).toFixed(1)} min)`}
                </div>
                <div>
                  <b>Border crossings:</b> {lastResult.totalBorderCrossings ?? '—'}
                </div>
                <div>
                  <b>Zone crossings:</b> {lastResult.totalZoneCrossings ?? '—'}
                </div>
                <div>
                  <b>Server wall time:</b>{' '}
                  {lastResult.requestWallTimeMs !== undefined ? `${lastResult.requestWallTimeMs} ms` : '—'}
                </div>
              </Box>
            </Grid>
          )}

          <Grid>
            <Box textAlign="center">
              <Button variant="contained" color="primary" type="submit" fullWidth disabled={submitting}>
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Find Path'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
};

export default SmartPathForm;
